import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AppContext';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { Plus, Pencil, Trash2, Search, Globe, Hash, Layers, X, Package } from 'lucide-react';
import toast from 'react-hot-toast';

const COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16','#f97316','#0ea5e9'];
const initForm = { code:'', name:'', url:'', description:'', color:'#6366f1', is_active:1 };

export default function SuperAdminPanels() {
  const { api } = useAuth();
  const [panels, setPanels]     = useState([]);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState(initForm);
  const [editId, setEditId]     = useState(null);
  const [delId, setDelId]       = useState(null);
  const [delLoading, setDelLoading] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const [modules, setModules]   = useState([]);
  const [modForm, setModForm]   = useState({ module_key:'', module_name:'', sort_order:0 });
  const [modSaving, setModSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/superadmin/panels'); setPanels(data); }
    catch { toast.error('Failed to load panels'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(initForm); setEditId(null); setModal('form'); };
  const openEdit   = p => { setForm({ code:p.code, name:p.name, url:p.url||'', description:p.description||'', color:p.color||'#6366f1', is_active:p.is_active }); setEditId(p.id); setModal('form'); };

  const openModules = async p => {
    setActivePanel(p); setModal('modules');
    try { const { data } = await api.get('/superadmin/panels/' + p.id + '/modules'); setModules(data); }
    catch { toast.error('Failed to load modules'); }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      if (editId) { await api.put('/superadmin/panels/' + editId, form); toast.success('Panel updated'); }
      else        { await api.post('/superadmin/panels', form);           toast.success('Panel created'); }
      setModal(null); load();
    } catch (err) { toast.error(err?.response?.data?.message || 'Error'); }
  };

  const handleDelete = async () => {
    setDelLoading(true);
    try { await api.delete('/superadmin/panels/' + delId); toast.success('Deleted'); setDelId(null); load(); }
    catch (err) { toast.error(err?.response?.data?.message || 'Error'); }
    finally { setDelLoading(false); }
  };

  const handleAddModule = async e => {
    e.preventDefault(); setModSaving(true);
    try {
      await api.post('/superadmin/panels/' + activePanel.id + '/modules', modForm);
      toast.success('Module added');
      const { data } = await api.get('/superadmin/panels/' + activePanel.id + '/modules');
      setModules(data);
      setModForm({ module_key:'', module_name:'', sort_order:0 });
    } catch (err) { toast.error(err?.response?.data?.message || 'Error'); }
    finally { setModSaving(false); }
  };

  const handleDelModule = async id => {
    try {
      await api.delete('/superadmin/panels/' + activePanel.id + '/modules/' + id);
      setModules(m => m.filter(x => x.id !== id)); toast.success('Removed');
    } catch { toast.error('Failed'); }
  };

  const filtered = panels.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.code.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Panels</h1><p className="page-subtitle">Create panels, set their URL, code and manage modules</p></div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={16}/> New Panel</button>
      </div>

      <div style={{ display:'flex', gap:12, marginBottom:20 }}>
        <div className="search-bar" style={{ flex:1 }}>
          <Search size={15} color="var(--text-muted)"/>
          <input placeholder="Search by name or code…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
      </div>

      {loading ? (
        <div className="flex-center" style={{padding:60}}><span className="spinner" style={{width:32,height:32}}/></div>
      ) : filtered.length === 0 ? (
        <div className="card"><div className="empty-state"><Layers size={40}/><h3>No panels found</h3><p>Create your first panel to get started</p><button className="btn btn-primary mt-4" onClick={openCreate}><Plus size={15}/> Create Panel</button></div></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Panel</th><th>Code</th><th>URL</th><th>Modules</th><th>Admins</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div style={{ width:38, height:38, borderRadius:'var(--r-md)', background:p.color+'22', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <span style={{ fontSize:'.9rem', fontWeight:800, color:p.color }}>{p.name[0]}</span>
                      </div>
                      <div>
                        <div style={{ fontWeight:600 }}>{p.name}</div>
                        <div className="text-muted text-sm">{p.description?.slice(0,38) || '—'}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className="badge badge-panel" style={{ fontFamily:'monospace' }}><Hash size={10}/>{p.code}</span></td>
                  <td>{p.url ? <a href={p.url} target="_blank" rel="noreferrer" style={{ color:'var(--brand)', fontSize:'.8rem', display:'flex', alignItems:'center', gap:4 }}><Globe size={12}/>{p.url.replace(/https?:\/\//,'').slice(0,28)}</a> : <span className="text-muted text-sm">—</span>}</td>
                  <td><span className="badge badge-warn"><Package size={10}/>{p.module_count||0}</span></td>
                  <td><span className="text-muted text-sm">{p.admin_count||0} admins</span></td>
                  <td><span className={`badge ${p.is_active ? 'badge-active':'badge-inactive'}`}>{p.is_active?'Active':'Inactive'}</span></td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn btn-ghost btn-sm btn-icon" title="Modules" onClick={()=>openModules(p)}><Layers size={14}/></button>
                      <button className="btn btn-ghost btn-sm btn-icon" title="Edit" onClick={()=>openEdit(p)}><Pencil size={14}/></button>
                      <button className="btn btn-ghost btn-sm btn-icon" style={{color:'var(--danger)'}} title="Delete" onClick={()=>setDelId(p.id)}><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Panel Form Modal */}
      <Modal open={modal==='form'} onClose={()=>setModal(null)} title={editId ? 'Edit Panel' : 'Create Panel'}>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Code <span className="req">*</span></label>
                <input className="form-input" placeholder="ERP01" value={form.code} onChange={e=>setForm(f=>({...f,code:e.target.value.toUpperCase()}))} required/>
              </div>
              <div className="form-group">
                <label className="form-label">Name <span className="req">*</span></label>
                <input className="form-input" placeholder="ERP Panel" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} required/>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Panel URL</label>
              <input className="form-input" type="url" placeholder="https://panel.example.com" value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-textarea" rows={3} placeholder="Brief description…" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">Color</label>
              <div className="color-swatches">
                {COLORS.map(c => <div key={c} className={`color-swatch ${form.color===c?'selected':''}`} style={{background:c}} onClick={()=>setForm(f=>({...f,color:c}))}/>)}
              </div>
            </div>
            {editId && (
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.is_active} onChange={e=>setForm(f=>({...f,is_active:+e.target.value}))}>
                  <option value={1}>Active</option><option value={0}>Inactive</option>
                </select>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={()=>setModal(null)}>Cancel</button>
            <button type="submit" className="btn btn-primary">{editId?'Update':'Create'}</button>
          </div>
        </form>
      </Modal>

      {/* Modules Modal */}
      <Modal open={modal==='modules'} onClose={()=>setModal(null)} title={'Modules — ' + (activePanel?.name||'')} size="modal-lg">
        <div className="modal-body">
          <div className="card card-pad mb-6" style={{background:'var(--bg-base)'}}>
            <h3 style={{fontSize:'.875rem',fontWeight:700,marginBottom:14}}>Add Module</h3>
            <form onSubmit={handleAddModule}>
              <div className="form-row-3">
                <div className="form-group" style={{marginBottom:0}}>
                  <label className="form-label">Key <span className="req">*</span></label>
                  <input className="form-input" placeholder="products" value={modForm.module_key} onChange={e=>setModForm(f=>({...f,module_key:e.target.value}))} required/>
                </div>
                <div className="form-group" style={{marginBottom:0}}>
                  <label className="form-label">Name <span className="req">*</span></label>
                  <input className="form-input" placeholder="Products" value={modForm.module_name} onChange={e=>setModForm(f=>({...f,module_name:e.target.value}))} required/>
                </div>
                <div className="form-group" style={{marginBottom:0}}>
                  <label className="form-label">Order</label>
                  <input className="form-input" type="number" min="0" value={modForm.sort_order} onChange={e=>setModForm(f=>({...f,sort_order:+e.target.value}))}/>
                </div>
              </div>
              <button type="submit" className="btn btn-primary mt-4" style={{width:'100%',justifyContent:'center'}} disabled={modSaving}>
                {modSaving ? <span className="spinner"/> : <><Plus size={14}/> Add Module</>}
              </button>
            </form>
          </div>
          {modules.length === 0 ? (
            <div className="empty-state"><Package size={32}/><h3>No modules yet</h3><p>Add modules to define permissions for this panel</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>#</th><th>Key</th><th>Module Name</th><th>Order</th><th></th></tr></thead>
                <tbody>
                  {modules.map((m,i) => (
                    <tr key={m.id}>
                      <td className="text-muted text-sm">{i+1}</td>
                      <td><code style={{background:'var(--bg-base)',padding:'2px 8px',borderRadius:4,fontSize:'.78rem',color:'var(--brand)'}}>{m.module_key}</code></td>
                      <td style={{fontWeight:600}}>{m.module_name}</td>
                      <td className="text-muted text-sm">{m.sort_order}</td>
                      <td><button className="btn btn-ghost btn-sm btn-icon" style={{color:'var(--danger)'}} onClick={()=>handleDelModule(m.id)}><X size={14}/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(null)}>Done</button></div>
      </Modal>

      <ConfirmDialog open={!!delId} onClose={()=>setDelId(null)} onConfirm={handleDelete} loading={delLoading}
        message="This panel will be soft-deleted and hidden from all active views."/>
    </div>
  );
}
